/**
 * 충전 패키지 관리 (CRUD)
 *
 *  사장님 크레딧 충전 화면에 노출되는 패키지 카탈로그.
 *  추가/수정/비활성/노출 기간 설정 가능. 한정 이벤트 패키지도 동일 인프라.
 */
import { useEffect, useState } from 'react';
import { Plus, Edit3, EyeOff, Eye, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { creditApi, CreditPackage } from '@/services/api/credit';

const formatKrw = (n: number) => `₩${n.toLocaleString()}`;

/* ─────────────────────────────────────────────────────────────────
 *  드래그앤드롭 정렬 헬퍼 — HTML5 Drag and Drop API
 * ───────────────────────────────────────────────────────────────── */

/** 배열의 from 인덱스 요소를 to 위치로 옮긴 새 배열 반환. */
const reorderArray = <T,>(list: T[], from: number, to: number): T[] => {
  if (from === to) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export function CreditPackagesPage() {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [editing, setEditing] = useState<CreditPackage | "new" | null>(null);
  const [loading, setLoading] = useState(true);
  const [dragFromIdx, setDragFromIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setPackages(await creditApi.listPackages());
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  /** 드래그 종료 시 서버에 새 순서 전송 (낙관적 UI). */
  const handleDrop = async (toIdx: number) => {
    if (dragFromIdx === null || dragFromIdx === toIdx) {
      setDragFromIdx(null);
      setDragOverIdx(null);
      return;
    }
    const next = reorderArray(packages, dragFromIdx, toIdx);
    setPackages(next);                  // optimistic update
    setDragFromIdx(null);
    setDragOverIdx(null);
    setSaving(true);
    try {
      await creditApi.reorderPackages(next.map((p) => p.packageSeq));
    } catch (e) {
      alert('정렬 저장 실패 — 새로고침합니다');
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">충전 패키지</h1>
          <p className="mt-1 text-sm text-gray-500">
            사장님 크레딧 화면에 노출되는 충전 패키지 카탈로그입니다.
          </p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")}>
          <Plus size={14} className="mr-1" />
          패키지 추가
        </Button>
      </header>

      <div className="mb-2 text-xs text-gray-500">
        💡 좌측 <GripVertical size={11} className="inline" /> 핸들을 드래그해서 순서를 바꿀 수 있어요
        {saving && <span className="ml-2 text-primary-600">· 저장 중…</span>}
      </div>

      {/* 패키지 테이블 — PaymentList 와 통일된 스타일 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-3 py-3"></th>
                <th className="px-6 py-3 w-[240px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  패키지명
                </th>
                <th className="px-6 py-3 w-[130px] text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  크레딧
                </th>
                <th className="px-6 py-3 w-[140px] text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  가격
                </th>
                <th className="px-6 py-3 w-[110px] text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  단가
                </th>
                <th className="px-6 py-3 w-[100px] text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  할인
                </th>
                <th className="px-6 py-3 w-[100px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  상태
                </th>
                <th className="px-6 py-3 w-[160px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-gray-500">로딩 중…</td></tr>
              )}
              {!loading && packages.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-gray-500">등록된 패키지가 없어요.</td></tr>
              )}
              {!loading && packages.map((p, idx) => (
                <tr
                  key={p.packageSeq}
                  draggable
                  onDragStart={() => setDragFromIdx(idx)}
                  onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                  onDragLeave={() => setDragOverIdx((cur) => (cur === idx ? null : cur))}
                  onDrop={(e) => { e.preventDefault(); handleDrop(idx); }}
                  onDragEnd={() => { setDragFromIdx(null); setDragOverIdx(null); }}
                  className={`
                    transition-colors
                    ${dragFromIdx === idx ? 'opacity-50' : ''}
                    ${dragOverIdx === idx && dragFromIdx !== idx ? 'bg-primary-50 border-t-2 border-t-primary-500' : 'hover:bg-gray-50'}
                  `}
                >
                  <td className="w-12 px-3 py-4 text-center cursor-grab text-gray-400 hover:text-gray-700">
                    <GripVertical size={16} className="inline" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="font-medium text-gray-900 truncate">{p.packageName}</div>
                    <div className="text-xs text-gray-400">SEQ {p.packageSeq}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-gray-900 tabular-nums">
                    {p.amount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-gray-900 tabular-nums">
                    {formatKrw(p.priceKrw)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-xs text-gray-500 tabular-nums">
                    ₩{Math.round(p.priceKrw / p.amount)}/크
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {p.discountRate ? (
                      <span className="font-semibold text-emerald-600 tabular-nums">
                        {Math.round(p.discountRate * 100)}%
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {p.activeYn === 'Y' ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        활성
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                        비활성
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => setEditing(p)}
                        className="rounded-md px-2 py-1 text-xs text-primary-600 hover:bg-primary-50"
                      >
                        <Edit3 size={12} className="inline" /> 수정
                      </button>
                      {p.activeYn === 'Y' ? (
                        <button
                          onClick={async () => {
                            if (!confirm(`"${p.packageName}" 패키지를 비활성화 하시겠어요?`)) return;
                            await creditApi.deactivatePackage(p.packageSeq);
                            load();
                          }}
                          className="rounded-md px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                        >
                          <EyeOff size={12} className="inline" /> 비활성
                        </button>
                      ) : (
                        <button
                          onClick={async () => {
                            if (!confirm(`"${p.packageName}" 패키지를 활성화 하시겠어요?`)) return;
                            await creditApi.activatePackage(p.packageSeq);
                            load();
                          }}
                          className="rounded-md px-2 py-1 text-xs text-emerald-600 hover:bg-emerald-50"
                        >
                          <Eye size={12} className="inline" /> 활성화
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <PackageEditModal
          pkg={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  패키지 추가/수정 모달
 * ───────────────────────────────────────────────────────────────── */
function PackageEditModal({
  pkg,
  onClose,
  onSaved,
}: {
  pkg: CreditPackage | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !pkg;
  // 정렬 순서(dataOrder) 는 드래그앤드롭으로만 변경. 신규는 99 (목록 맨 뒤),
  // 수정은 기존 값 그대로 유지 — 모달에서는 노출/입력 X.
  const [form, setForm] = useState({
    packageName: pkg?.packageName ?? '',
    amount: pkg?.amount ?? 100,
    priceKrw: pkg?.priceKrw ?? 9900,
    discountRate: pkg?.discountRate ?? null,
    activeYn: (pkg?.activeYn ?? 'Y') as 'Y' | 'N',
    dtVisibleFrom: pkg?.dtVisibleFrom?.slice(0, 10) ?? '',
    dtVisibleTo: pkg?.dtVisibleTo?.slice(0, 10) ?? '',
  });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // 사유 선택 — 입력하지 않아도 저장 가능. 입력 시에만 5자 이상 검증.
  const reasonTrimmed = reason.trim();
  const reasonInvalid = reasonTrimmed.length > 0 && reasonTrimmed.length < 5;
  const canSave =
    form.packageName.trim().length > 0 &&
    form.amount > 0 &&
    form.priceKrw > 0 &&
    !reasonInvalid &&
    !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      // dataOrder 는 BE 가 기존 값 유지 (수정) 또는 list 끝(신규)에 자동 배치.
      // 정렬 순서는 화면에서 드래그앤드롭으로만 변경.
      const payload = {
        packageName: form.packageName,
        amount: form.amount,
        priceKrw: form.priceKrw,
        discountRate: form.discountRate,
        activeYn: form.activeYn,
        dtVisibleFrom: form.dtVisibleFrom ? new Date(form.dtVisibleFrom).toISOString() : null,
        dtVisibleTo: form.dtVisibleTo ? new Date(form.dtVisibleTo).toISOString() : null,
      } as any;
      if (isNew) {
        await creditApi.createPackage(payload, reason);
      } else {
        await creditApi.updatePackage(pkg!.packageSeq, payload, reason);
      }
      onSaved();
    } catch (e) {
      alert('저장 실패: ' + (e instanceof Error ? e.message : String(e)));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={isNew ? '패키지 추가' : '패키지 수정'} size="lg">
      <div className="space-y-4">
        <Field label="패키지명" required>
          <input
            type="text"
            value={form.packageName}
            onChange={(e) => setForm({ ...form, packageName: e.target.value })}
            placeholder="예: Starter / Popular / 신년특가"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="크레딧 양" required>
            <input
              type="number"
              min={1}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: parseInt(e.target.value) || 0 })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg font-bold focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </Field>
          <Field label="가격 (KRW)" required>
            <input
              type="number"
              min={1}
              value={form.priceKrw}
              onChange={(e) => setForm({ ...form, priceKrw: parseInt(e.target.value) || 0 })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg font-bold focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </Field>
          <Field label="할인율 (0~1)">
            <input
              type="number"
              step={0.01}
              min={0}
              max={1}
              value={form.discountRate ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  discountRate: e.target.value === '' ? null : parseFloat(e.target.value),
                })
              }
              placeholder="예: 0.21"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="노출 시작 (선택)">
            <input
              type="date"
              value={form.dtVisibleFrom}
              onChange={(e) => setForm({ ...form, dtVisibleFrom: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </Field>
          <Field label="노출 종료 (선택)">
            <input
              type="date"
              value={form.dtVisibleTo}
              onChange={(e) => setForm({ ...form, dtVisibleTo: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </Field>
        </div>

        <Field label="변경 사유 (선택)">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="예: 신년 이벤트 한정 40% 할인 패키지 추가 (선택사항)"
            className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 ${
              reasonInvalid ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-primary-500'
            }`}
          />
          {reasonInvalid ? (
            <p className="mt-1 text-xs text-red-600">입력하실 경우 5자 이상이어야 합니다 (또는 비워두세요).</p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">비워두면 "(사유 미입력)" 으로 감사 로그에 기록됩니다.</p>
          )}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave} loading={saving}>
            {isNew ? '추가' : '저장'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
